import type { AppliedBy } from "@unbox/api";
import { type ReactNode, useState } from "react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { useCurrency } from "@/hooks/use-currency";
import { describeRuleLogic } from "@/lib/rules/rule-logic";
import { type RuleSignalAction, ruleSignalCopy } from "@/lib/rules/rule-signal-copy";
import { RuleBuilder } from "@/pages/rules/rule-builder";

export const RULE_TOOLTIP_CLASSNAME =
  "z-[100] w-auto max-w-[320px] rounded-lg border-0 bg-primary-solid px-3 py-2 text-xs font-semibold text-white";

const RuleLink = ({ name, onOpen }: { name: string; onOpen: () => void }) => (
  <button
    type="button"
    className="cursor-pointer text-left font-semibold underline"
    onClick={(e) => {
      e.stopPropagation();
      onOpen();
    }}
  >
    {name}
  </button>
);

type RuleSourceTooltipProps = {
  appliedBy: AppliedBy | null | undefined;
  action: RuleSignalAction;
  children: ReactNode;
};

export const RuleSourceTooltip = ({ appliedBy, action, children }: RuleSourceTooltipProps) => {
  const currency = useCurrency();
  const [ruleOpen, setRuleOpen] = useState(false);

  if (!appliedBy) return <>{children}</>;

  const copy = ruleSignalCopy(action, appliedBy);
  const logic = appliedBy.source === "rule" ? describeRuleLogic(appliedBy.rule, currency) : "";

  return (
    <>
      <HoverCard openDelay={100} closeDelay={150}>
        <HoverCardTrigger asChild>{children}</HoverCardTrigger>
        <HoverCardContent side="top" className={RULE_TOOLTIP_CLASSNAME}>
          {appliedBy.source === "rule" ? (
            <p>
              {copy} <RuleLink name={appliedBy.rule.name} onOpen={() => setRuleOpen(true)} />
              {logic ? `: ${logic}` : ""}
            </p>
          ) : (
            <p>{appliedBy.user ? `${copy} by ${appliedBy.user.firstName} ${appliedBy.user.lastName}` : copy}</p>
          )}
        </HoverCardContent>
      </HoverCard>
      {ruleOpen && appliedBy.source === "rule" && (
        <RuleBuilder area={appliedBy.rule.area} ruleId={appliedBy.rule.ruleId} open onOpenChange={setRuleOpen} />
      )}
    </>
  );
};
